import tippy from "tippy.js";
import { EMPTY_COMMENTS } from "@/api/staticPaths";
import { canv } from "@/canv/global";
import { commentResolveIsDisabled, setClick } from "@/canv/utils";
import { saveTextToClipboard, setViewSettings } from "@/canvasController/helpers";
import { SHOW_COMMENTS_LS_KEY, SHOW_RESOLVED_COMMENTS_LS_KEY } from "@/constants";
import { CommentWidget } from "@/elements/comment";
import { decodeHTMLEntities } from "@/elements/comment/utils";
import { eventEmitter } from "@/eventEmitter";
import { generateColor } from "@/helpers/generateColor";
import { getInitials } from "@/helpers/getInitials";
import { t } from "@/i18n";
import { getDate } from "@/utils";

type insertAdjacentHTMLPosition = "afterbegin" | "afterend" | "beforebegin" | "beforeend";

export class CommentsWindow {
    open() {
        canv.appInterface.voteWindow.close();

        if (this.opened) {
            this.close();
            return;
        }

        this.opened = true;

        eventEmitter.dispatch("header.button.active.name", {
            name: "comments",
        });

        this.sortingCommentsEntities();

        this.createTemplate();

        this.setButtons();

        this.init();
    }

    setButtons() {
        this.settingsButton = document.querySelector(".commentsWindowSettings");
        this.sortingButton = document.querySelector(".commentsWindowSorting");

        this.settingsMenu = document.querySelector(".commentsSettingsMenu");
        this.sortingMenu = document.querySelector(".commentsSortingMenu");
    }

    sortingCommentsEntities() {
        const comments = canv.canvasController.items.reduce<CommentWidget[]>((acc, curr) => {
            if (curr.isItemWrapper() && curr.children[0] instanceof CommentWidget) {
                acc.push(curr.children[0]);
                return acc;
            }
            return acc;
        }, []);

        if (this.sort === "lastUpdatedSort") {
            this.comments = comments.sort((a, b) => {
                if (!b.updatedAt || !a.updatedAt) {
                    return 1;
                }
                return +new Date(b.updatedAt) - +new Date(a.updatedAt);
            });
        } else {
            this.comments = comments.reverse();
        }
    }

    createTemplate() {
        const bottomBarWrapper = document.querySelector(".bottomBarWrapper");

        const commentsWindowWrapper = document.createElement("div");

        if (bottomBarWrapper) {
            bottomBarWrapper.append(commentsWindowWrapper);
        }

        commentsWindowWrapper.outerHTML = this.getWindow(this.comments.length > 0);

        const commentsList = document.querySelector(".commentsList");

        if (commentsList) {
            for (let i = 0; i < this.comments.length; i++) {
                commentsList.innerHTML += this.commentTemplate(this.comments[i]);
            }
        }
    }

    removeAllListItems() {
        const items = document.querySelectorAll(".commentsItem");

        for (let i = 0; i < items.length; i++) {
            items[i].removeEventListener("mousedown", this._onItemClick, false);

            const resolveButton = items[i].querySelector(".commentResolveButton");

            if (resolveButton) {
                resolveButton.removeEventListener("mousedown", this._onResolve);
            }

            const moreActionsButton = items[i].querySelector(".commentMoreActionsButton");

            if (moreActionsButton) {
                moreActionsButton.removeEventListener("mousedown", this._onMoreActions);
            }
        }
    }

    close() {
        this.removeAllListItems();

        document.removeEventListener("click", (e) => this.handleClickOutsideMenu(e), false);
        document.removeEventListener("click", (e) => this.closeCommentActionsModals(e, false));

        const commentsWindow = document.querySelector(".commentsWindow");

        if (commentsWindow) {
            commentsWindow.outerHTML = "";
        }

        this.opened = false;

        eventEmitter.dispatch("header.button.active.name", {
            name: null,
        });
    }

    itemClick(commentUID: string) {
        canv.commentsPopovers.hideCommentsPopovers();
        const comment = this.comments.find((el) => el.UID === commentUID);

        if (!comment) return;

        const prevSelectedComment = document.getElementById(this.selectedCommentUID || "0");
        const newSelectedComment = document.getElementById(commentUID);
        if (prevSelectedComment) {
            prevSelectedComment.classList.remove("selected");
        }
        if (newSelectedComment) {
            newSelectedComment.classList.add("selected");
            this.selectedCommentUID = commentUID;
        }

        if (!canv.canvasController.showComments) {
            const showOnCanvasSwitch = document.querySelector(".showOnCanvasSwitch") as
                | HTMLInputElement
                | undefined;
            showOnCanvasSwitch?.click();
        }

        canv.zoomToComment([comment.parent]);
        (comment as CommentWidget).TryAction("mouseup");
    }

    itemResolve(uid: string, e: Event) {
        e.stopPropagation();
        const commentResolveButton = document
            .getElementById(uid)
            ?.querySelector(".commentResolveButton");
        if (commentResolveButton) {
            const isDisabled = commentResolveButton.classList.contains("disabled");
            if (isDisabled) return;

            const targetEntity = this.comments.find((comment) => comment.UID === uid);
            if (!targetEntity) return;

            targetEntity.setResolved(!targetEntity.isResolved);
        }
    }

    closeCommentActionsModals(e, force: boolean) {
        if (!force) {
            if (!e || e.target.classList.contains("commentMoreActionsButton")) return;
        }
        const commentActionsModals = document.querySelectorAll(".commentActionsModal");

        for (let i = 0; i < commentActionsModals.length; i++) {
            commentActionsModals[i].parentElement?.classList.remove("active");
            commentActionsModals[i].parentElement?.removeChild(commentActionsModals[i]);
        }
    }

    openItemActions(commentUID: string, e: Event) {
        e.stopPropagation();
        const comment = document.getElementById(commentUID);
        if (!comment) return;

        const commentActionsButton = comment?.querySelector(".commentMoreActionsButton");
        const commentActionsModal = commentActionsButton?.querySelector(".commentActionsModal");

        if (commentActionsButton?.classList.contains("active")) {
            if (commentActionsModal) {
                const copyLinkAction =
                    commentActionsButton?.querySelector(".commentActionCopyLink");
                copyLinkAction?.removeEventListener("mousedown", this._onItemMenuCopyLink);

                const deleteAction = commentActionsButton?.querySelector(".commentActionDelete");
                deleteAction?.removeEventListener("mousedown", this._onItemMenuDelete);

                commentActionsButton?.removeChild(commentActionsModal);
            }
            commentActionsButton.classList.remove("active");
            this.closeCommentActionsModals(e, true);
        } else {
            this.closeCommentActionsModals(e, true);
            if (commentActionsButton) {
                commentActionsButton.innerHTML += this.initActionsModal();
                commentActionsButton.classList.add("active");
            }

            const copyLinkAction = commentActionsButton?.querySelector(".commentActionCopyLink");
            copyLinkAction?.addEventListener("mousedown", this._onItemMenuCopyLink);

            const deleteAction = commentActionsButton?.querySelector(".commentActionDelete");
            deleteAction?.addEventListener("mousedown", this._onItemMenuDelete);
        }
    }

    // copy comment link
    itemMenuCopyLink(commentUID: string, e: Event) {
        const pathToGroup = `${canv.host}/board/${canv.getInitDataProp("boardUID")}?linkToWidget=${commentUID}`;
        saveTextToClipboard(pathToGroup);
    }

    initActionsModal() {
        const showDelete = canv.isBoardModeEditOrOwner();

        return `
            <div class="commentActionsModal">
                <div class="commentActionItem commentActionCopyLink">${t("comments.actions.copyLink")}</div>
                ${showDelete ? `<div class="commentActionItem commentActionDelete">${t("comments.actions.delete")}</div>` : ""}
            </div>
        `;
    }

    handleShowOnCanvasSwitchChange(e: Event) {
        if (!(e.target instanceof HTMLInputElement)) return;
        const checkbox = e.target;
        canv.canvasController.showComments = checkbox.checked;
        setViewSettings(SHOW_COMMENTS_LS_KEY, checkbox.checked);

        if (!checkbox.checked) {
            canv.commentsPopovers.hideCommentsPopovers("window");
        }

        canv.redraw(true);
    }

    handleShowResolvedSwitchChange(e: Event) {
        if (!(e.target instanceof HTMLInputElement)) return;
        const checkbox = e.target;
        canv.canvasController.showResolvedComments = checkbox.checked;
        setViewSettings(SHOW_RESOLVED_COMMENTS_LS_KEY, checkbox.checked);
        canv.redraw(true);

        const listItems = document.querySelectorAll(".commentsItem");
        for (let i = 0; i < listItems.length; i++) {
            if (!listItems[i].classList.contains("resolved")) continue;
            if (checkbox.checked) {
                listItems[i].classList.remove("hidden");
            } else {
                listItems[i].classList.add("hidden");
            }
        }
    }

    handleSortChange(e: Event) {
        if (!(e.target instanceof HTMLInputElement)) return;
        this.sort = e.target.value;

        this.sortingCommentsEntities();
        // перерендер всего списка
        this.removeAllListItems();

        const commentsList = document.querySelector(".commentsList");
        if (commentsList) {
            commentsList.innerHTML = "";
            for (let i = 0; i < this.comments.length; i++) {
                commentsList.innerHTML += this.commentTemplate(this.comments[i]);
            }
        }

        this.initCommentListItemsListeners();

        this.createTippies();
    }

    handleClickOutsideMenu(e: Event) {
        if (this.settingsButton && this.sortingButton && this.settingsMenu && this.sortingMenu) {
            const isInsideSettingsClick =
                this.settingsButton.contains(e.target as Node) ||
                this.settingsMenu.contains(e.target as Node);
            const isInsideSortingClick =
                this.sortingButton.contains(e.target as Node) ||
                this.sortingMenu.contains(e.target as Node);

            if (!isInsideSettingsClick && this.settingsButton.classList.contains("active")) {
                this.settingsButton.classList.remove("active");
                this.settingsMenu?.classList?.remove("visible");
            }

            if (!isInsideSortingClick && this.sortingButton.classList.contains("active")) {
                this.sortingButton.classList.remove("active");
                this.sortingMenu?.classList?.remove("visible");
            }
        }
    }

    initCommentSettingsListeners() {
        if (this.settingsButton) {
            this.settingsButton.addEventListener(
                "mousedown",
                (e) => this.openCloseSettingsMenu(e),
                false,
            );
        }

        const showOnCanvasSwitch = document.querySelector(
            ".showOnCanvasSwitch",
        ) as HTMLInputElement | null;
        const showResolvedSwitch = document.querySelector(
            ".showResolvedSwitch",
        ) as HTMLInputElement | null;

        if (showOnCanvasSwitch) {
            showOnCanvasSwitch.addEventListener("change", this.handleShowOnCanvasSwitchChange);
            showOnCanvasSwitch.checked = canv.canvasController.showComments;
        }

        if (showResolvedSwitch) {
            showResolvedSwitch.addEventListener("change", this.handleShowResolvedSwitchChange);
            showResolvedSwitch.checked = canv.canvasController.showResolvedComments;
        }
    }

    initCommentSortingListeners() {
        const createSortRadio = document.querySelector(".createSort") as
            | HTMLInputElement
            | undefined;
        const lastUpdatedSortRadio = document.querySelector(".lastUpdatedSort") as
            | HTMLInputElement
            | undefined;

        if (this.sortingButton) {
            this.sortingButton.addEventListener(
                "mousedown",
                (e) => this.openCloseSortingMenu(e),
                false,
            );
        }

        if (createSortRadio) {
            createSortRadio.addEventListener("change", (e) => this.handleSortChange(e));
        }

        if (lastUpdatedSortRadio) {
            lastUpdatedSortRadio.addEventListener("change", (e) => this.handleSortChange(e));
        }
    }

    initCommentListItemsListeners() {
        const items = document.querySelectorAll(".commentsItem");

        for (let i = 0; i < items.length; i++) {
            items[i].addEventListener("mousedown", this._onItemClick, false);

            const resolveButton = items[i].querySelector(".commentResolveButton");

            if (resolveButton) {
                resolveButton.addEventListener("mousedown", this._onResolve);
            }

            const moreActionsButton = items[i].querySelector(".commentMoreActionsButton");

            if (moreActionsButton) {
                moreActionsButton.addEventListener("mousedown", this._onMoreActions);
            }
        }
    }

    initOutSideClickListeners() {
        document.addEventListener("click", (e) => this.handleClickOutsideMenu(e), false);
    }

    createTippies() {
        if (canv.isBoardModeEditOrOwner() || canv.isBoardModeEditMini()) {
            tippy(".commentResolveButton", {
                onShow: (instance) => {
                    if (instance.reference.classList.contains("commentResolved")) {
                        instance.setContent(
                            canv.appInterface.createTooltipContent(
                                t("comments.tooltips.markUnresolved"),
                            ),
                        );
                    } else {
                        instance.setContent(
                            canv.appInterface.createTooltipContent(
                                t("comments.tooltips.markResolved"),
                            ),
                        );
                    }
                },
            });
        }

        tippy(".commentsWindowSorting", {
            content: canv.appInterface.createTooltipContent(t("comments.tooltips.sorting")),
        });

        tippy(".commentsWindowSettings", {
            content: canv.appInterface.createTooltipContent(t("comments.tooltips.settings")),
        });
    }

    init() {
        setClick("commentsWindowClose", () => this.close());

        this.initCommentSettingsListeners();

        this.initCommentSortingListeners();

        this.initCommentListItemsListeners();

        this.initOutSideClickListeners();

        this.createTippies();

        document.addEventListener("click", (e) => this.closeCommentActionsModals(e, false));
    }

    addNewComment(comment) {
        if (this.comments.length === 0) {
            this.sortingCommentsEntities();
            const commentsWindowEmptyNode = document.querySelector(".commentsWindowEmpty");
            if (commentsWindowEmptyNode) {
                commentsWindowEmptyNode.remove();
            }

            const commentsWindowNode = document.querySelector(".commentsWindowDividerWrapper");
            if (commentsWindowNode) {
                commentsWindowNode.insertAdjacentHTML(
                    <insertAdjacentHTMLPosition>"afterend",
                    this.getCommentsTemplate(),
                );
            }

            const commentsList = document.querySelector(".commentsList");
            if (commentsList) {
                for (let i = 0; i < this.comments.length; i++) {
                    commentsList.innerHTML += this.commentTemplate(this.comments[i]);
                }
            }
            this.initCommentListItemsListeners();
        } else {
            this.sortingCommentsEntities();
            const commentsList = document.querySelector(".commentsList");
            if (commentsList) {
                const newCommentNode = this.commentTemplate(comment);
                let position = "afterbegin";
                if (this.sort !== "createSort") {
                    position = "beforeend";
                }
                commentsList.insertAdjacentHTML(
                    <insertAdjacentHTMLPosition>position,
                    newCommentNode,
                );
            }
            this.removeAllListItems();
            this.initCommentListItemsListeners();
        }
    }

    deleteComment(commentUID: string) {
        this.sortingCommentsEntities();

        const commentNode = document.getElementById(commentUID);
        if (commentNode) {
            commentNode.removeEventListener("mousedown", () => this.itemClick(commentUID), false);

            const resolveButton = commentNode.querySelector(".commentResolveButton");
            if (resolveButton) {
                resolveButton.removeEventListener("mousedown", (e) =>
                    this.itemResolve(commentUID, e),
                );
            }
            const moreActionsButton = commentNode.querySelector(".commentMoreActionsButton");
            if (moreActionsButton) {
                moreActionsButton.removeEventListener("mousedown", (e) =>
                    this.openItemActions(commentUID, e),
                );
            }

            commentNode.remove();
        }

        if (this.comments.length === 0) {
            const commentsListNode = document.querySelector(".commentsList");
            if (commentsListNode) {
                commentsListNode.remove();
            }
            const commentsWindowNode = document.querySelector(".commentsWindowDividerWrapper");
            if (commentsWindowNode) {
                commentsWindowNode.insertAdjacentHTML(
                    <insertAdjacentHTMLPosition>"afterend",
                    this.getEmptyTemplate(),
                );
            }
        }
    }

    updateListItem(commentData: any) {
        const targetCommentNode = document.getElementById(commentData.UID);
        if (!targetCommentNode) return;

        this.sortingCommentsEntities();

        const commentResolveButton = targetCommentNode.querySelector(".commentResolveButton");

        if (commentData.isResolved && commentResolveButton) {
            targetCommentNode.classList.add("resolved");
            commentResolveButton.classList.add("commentResolved");
        } else {
            targetCommentNode.classList.remove("resolved");
            if (commentResolveButton) {
                commentResolveButton.classList.remove("commentResolved");
            }
        }

        const commentAnswersCount = targetCommentNode.querySelector(".commentAnswersCount");
        const count = commentData.messages.length;
        if (commentAnswersCount) {
            commentAnswersCount.innerHTML = count ? t("comments.answers", { count }) : "";
        }

        const comment = this.comments.find((comment) => comment.UID === commentData.UID);
        if (comment?.isCommentPopoverOpen("window")) {
            comment.updatePopover("window", true);
        }
    }

    updateCommentsList(commentData: any) {
        const commentsList = document.querySelector(".commentsList");
        const targetCommentNode = document.getElementById(commentData.UID);
        if (!commentsList || !targetCommentNode) return;

        this.sortingCommentsEntities();
        this.removeAllListItems();

        const commentIndex = this.comments.findIndex((comment) => comment.UID === commentData.UID);
        const nextComment = this.comments[commentIndex + 1];
        const nextNode = nextComment ? document.getElementById(nextComment.UID) : null;

        const comment = this.comments[commentIndex];
        const count = comment.messages.length;
        const commentAnswersCount = targetCommentNode.querySelector(".commentAnswersCount");
        if (commentAnswersCount) {
            commentAnswersCount.innerHTML = count > 0 ? t("comments.answers", { count }) : "";
            commentsList.insertBefore(targetCommentNode, nextNode);
        }
        this.initCommentListItemsListeners();
    }

    updateList(commentsActions: any, isApplied: boolean) {
        const commentsWindow = document.querySelector(".commentsWindow");
        if (!commentsWindow) return;
        commentsActions.forEach((action) => {
            if (action.action === "create") {
                if (isApplied) {
                    this.addNewComment(action.comment);
                } else {
                    this.deleteComment(action.comment.UID);
                }
            }

            if (action.action === "delete") {
                if (isApplied) {
                    this.deleteComment(action.comment.UID);
                } else {
                    this.addNewComment(action.comment);
                }
            }

            if (action.type === "property") {
                if (isApplied) {
                    this.updateListItem(action.state);
                }
            }

            if (
                action.type === "propertyBuild" &&
                (action.action === "add" || action.action === "remove")
            ) {
                if (isApplied) {
                    this.updateCommentsList(action);
                }
            }
        });
    }

    openCloseSettingsMenu(e: Event) {
        e.stopPropagation();
        if (this.settingsButton && this.sortingButton) {
            if (this.settingsButton.classList.contains("active")) {
                this.settingsButton.classList.remove("active");
                this.settingsMenu?.classList?.remove("visible");
            } else {
                this.settingsButton.classList.add("active");
                this.settingsMenu?.classList?.add("visible");
            }

            this.sortingButton.classList.remove("active");
            this.sortingMenu?.classList?.remove("visible");
        }
    }

    openCloseSortingMenu(e: Event) {
        e.stopPropagation();
        if (this.sortingButton && this.settingsButton) {
            if (this.sortingButton.classList.contains("active")) {
                this.sortingButton.classList.remove("active");
                this.sortingMenu?.classList?.remove("visible");
            } else {
                this.sortingButton.classList.add("active");
                this.sortingMenu?.classList?.add("visible");
            }

            this.settingsButton.classList.remove("active");
            this.settingsMenu?.classList?.remove("visible");
        }
    }

    getWindow(areComments = false) {
        return `
            <div class="commentsWindow" aria-labelledby="commentsWindowTitle">
                <div class="commentsWindowHeader">
                    <h2 class="commentsWindowTitle">${t("comments.title")}</h2>
                    <div class="commentsWindowFilters">
                        <div class="commentsWindowSortingWrapper">
                            <button class="commentsWindowSorting commentsWindowIconButton">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.6288 5.04706C11.7846 5.20269 11.9959 5.2901 12.2161 5.2901C12.4363 5.2901 12.6475 5.20269 12.8033 5.04706L13.6011 4.24927V15.5402C13.6011 15.7606 13.6887 15.972 13.8445 16.1278C14.0004 16.2837 14.2118 16.3712 14.4322 16.3712C14.6526 16.3712 14.8639 16.2837 15.0198 16.1278C15.1756 15.972 15.2632 15.7606 15.2632 15.5402V4.24927L16.061 5.04706C16.1371 5.12871 16.2288 5.1942 16.3307 5.23962C16.4327 5.28504 16.5427 5.30946 16.6543 5.31143C16.7659 5.3134 16.8767 5.29287 16.9802 5.25108C17.0837 5.20928 17.1777 5.14707 17.2566 5.06816C17.3355 4.98924 17.3977 4.89524 17.4395 4.79177C17.4813 4.68829 17.5018 4.57745 17.4999 4.46587C17.4979 4.35429 17.4735 4.24424 17.4281 4.1423C17.3826 4.04037 17.3172 3.94862 17.2355 3.87254L15.0194 1.65646C14.8636 1.50083 14.6524 1.41342 14.4322 1.41342C14.2119 1.41342 14.0007 1.50083 13.8449 1.65646L11.6288 3.87254C11.4732 4.02836 11.3858 4.23958 11.3858 4.4598C11.3858 4.68002 11.4732 4.89124 11.6288 5.04706ZM6.39887 15.7507L7.19666 14.9529C7.27274 14.8713 7.36449 14.8058 7.46642 14.7604C7.56836 14.715 7.67841 14.6905 7.78999 14.6886C7.90157 14.6866 8.01241 14.7071 8.11589 14.7489C8.21936 14.7907 8.31336 14.8529 8.39228 14.9318C8.47119 15.0108 8.5334 15.1048 8.5752 15.2082C8.61699 15.3117 8.63752 15.4226 8.63555 15.5341C8.63358 15.6457 8.60916 15.7558 8.56374 15.8577C8.51832 15.9596 8.45283 16.0514 8.37118 16.1275L6.1551 18.3435C5.99928 18.4992 5.78806 18.5866 5.56784 18.5866C5.34762 18.5866 5.1364 18.4992 4.98058 18.3435L2.7645 16.1275C2.68285 16.0514 2.61736 15.9596 2.57194 15.8577C2.52652 15.7558 2.5021 15.6457 2.50013 15.5341C2.49816 15.4226 2.51869 15.3117 2.56048 15.2082C2.60228 15.1048 2.66449 15.0108 2.7434 14.9318C2.82232 14.8529 2.91632 14.7907 3.01979 14.7489C3.12327 14.7071 3.23411 14.6866 3.34569 14.6886C3.45727 14.6905 3.56732 14.715 3.66926 14.7604C3.77119 14.8058 3.86294 14.8713 3.93902 14.9529L4.73681 15.7507V4.4598C4.73681 4.2394 4.82436 4.02802 4.98021 3.87217C5.13606 3.71633 5.34744 3.62877 5.56784 3.62877C5.78824 3.62877 5.99962 3.71633 6.15547 3.87217C6.31131 4.02802 6.39887 4.2394 6.39887 4.4598V15.7507Z" fill="#2C2D32"/>
                                </svg>
                            </button>
                            <div class="commentsSortingMenu">
                                <p class="commentsSortingMenuTitle">${t("comments.sorting.title")}</p>
                                <div class="commentsSortingMenuItem">
                                    <div class="sortRadio">
                                        <input class="createSort" id="sortRadio-1" type="radio" name="radio" value="createSort" checked>
                                        <label for="sortRadio-1">${t("comments.sorting.byCommentDate")}</label>
                                    </div>
                                </div>
                                <div class="commentsSortingMenuItem">
                                    <div class="sortRadio lastUpdatedSortRadio">
                                        <input class="lastUpdatedSort" id="sortRadio-2" type="radio" name="radio" value="lastUpdatedSort">
                                        <label for="sortRadio-2">${t("comments.sorting.byLastReplyDate")}</label>
                                    </div>
                                </div>
                            </div>
                        </div>


                        <div class="commentsWindowSettingsWrapper">
                            <button class="commentsWindowSettings commentsWindowIconButton">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17 4H7.1999M11 14H2M11 14C11 15.6569 12.3431 17 14 17C15.6569 17 17 15.6569 17 14C17 12.3431 15.6569 11 14 11C12.3431 11 11 12.3431 11 14ZM7 4C7 5.65685 5.65685 7 4 7C2.34315 7 1 5.65685 1 4C1 2.34315 2.34315 1 4 1C5.65685 1 7 2.34315 7 4Z" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                            <div class="commentsSettingsMenu">
                                <p class="commentsSettingsMenuTitle">${t("comments.display.title")}</p>
                                <div class="commentsSettingsMenuItem">
                                    <p>${t("comments.display.showOnBoard")}</p>
                                    <label class="switch">
                                        <input class="showOnCanvasSwitch" type="checkbox">
                                        <span class="slider round"></span>
                                    </label>
                                </div>
                                <div class="commentsSettingsMenuItem">
                                    <p>${t("comments.display.showResolved")}</p>
                                    <label class="switch">
                                        <input class="showResolvedSwitch" type="checkbox">
                                        <span class="slider round"></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <button class="commentsWindowClose commentsWindowIconButton">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16 4L4 16M4 4L16 16" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
                <div class="commentsWindowDividerWrapper">
                    <hr class="commentsWindowDivider" />
                </div>
                ${areComments ? this.getCommentsTemplate() : this.getEmptyTemplate()}
            </div>
        `;
    }

    commentTemplate(comment: any) {
        const answersCount = comment.messages.length;
        const classCommentsItem = `commentsItem${comment.isResolved ? " resolved" : ""}${!canv.canvasController.showResolvedComments && comment.isResolved ? " hidden" : ""}`;
        const classResolveButton = `commentResolveButton${commentResolveIsDisabled() ? " disabled" : ""}${comment.isResolved ? " commentResolved" : ""}`;

        const wrapper = document.createElement("div");
        wrapper.className = classCommentsItem;
        wrapper.id = comment.UID;

        const headDiv = document.createElement("div");
        headDiv.className = "commentHead";

        const avatarDiv = document.createElement("div");
        avatarDiv.className = "commentAuthorAvatar";
        const avatarInner = document.createElement("div");
        avatarInner.style.backgroundColor = generateColor(comment.token);
        avatarInner.textContent = getInitials(comment.accountName);
        avatarDiv.appendChild(avatarInner);

        const creatorDiv = document.createElement("div");
        creatorDiv.className = "commentCreater";
        const authorName = document.createElement("div");
        authorName.className = "commentAuthorName";
        authorName.textContent = comment.accountName;
        const createdAt = document.createElement("div");
        createdAt.className = "commentCreatedAt";
        createdAt.textContent = getDate(new Date(comment.createdAt));
        creatorDiv.appendChild(authorName);
        creatorDiv.appendChild(createdAt);

        const buttonsDiv = document.createElement("div");
        buttonsDiv.className = "commentButtons";

        const resolveBtn = document.createElement("div");
        resolveBtn.className = classResolveButton;
        resolveBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 18.75C14.8325 18.75 18.75 14.8325 18.75 10C18.75 5.16751 14.8325 1.25 10 1.25C5.16751 1.25 1.25 5.16751 1.25 10C1.25 14.8325 5.16751 18.75 10 18.75Z" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round"/>
                <path d="M14.1384 7.34589L8.83065 12.6545L5.86133 9.68994" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;

        const moreActionsBtn = document.createElement("div");
        moreActionsBtn.className = "commentMoreActionsButton";
        moreActionsBtn.innerHTML = `
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.00033 8.66659C8.36852 8.66659 8.66699 8.36811 8.66699 7.99992C8.66699 7.63173 8.36852 7.33325 8.00033 7.33325C7.63214 7.33325 7.33366 7.63173 7.33366 7.99992C7.33366 8.36811 7.63214 8.66659 8.00033 8.66659Z" fill="#2C2D32"/>
                <path d="M12.667 8.66659C13.0352 8.66659 13.3337 8.36811 13.3337 7.99992C13.3337 7.63173 13.0352 7.33325 12.667 7.33325C12.2988 7.33325 12.0003 7.63173 12.0003 7.99992C12.0003 8.36811 12.2988 8.66659 12.667 8.66659Z" fill="#2C2D32"/>
                <path d="M3.33366 8.66659C3.70185 8.66659 4.00033 8.36811 4.00033 7.99992C4.00033 7.63173 3.70185 7.33325 3.33366 7.33325C2.96547 7.33325 2.66699 7.63173 2.66699 7.99992C2.66699 8.36811 2.96547 8.66659 3.33366 8.66659Z" fill="#2C2D32"/>
                <path d="M8.00033 8.66659C8.36852 8.66659 8.66699 8.36811 8.66699 7.99992C8.66699 7.63173 8.36852 7.33325 8.00033 7.33325C7.63214 7.33325 7.33366 7.63173 7.33366 7.99992C7.33366 8.36811 7.63214 8.66659 8.00033 8.66659Z" stroke="#2C2D32" stroke-width="0.866667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12.667 8.66659C13.0352 8.66659 13.3337 8.36811 13.3337 7.99992C13.3337 7.63173 13.0352 7.33325 12.667 7.33325C12.2988 7.33325 12.0003 7.63173 12.0003 7.99992C12.0003 8.36811 12.2988 8.66659 12.667 8.66659Z" stroke="#2C2D32" stroke-width="0.866667" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M3.33366 8.66659C3.70185 8.66659 4.00033 8.36811 4.00033 7.99992C4.00033 7.63173 3.70185 7.33325 3.33366 7.33325C2.96547 7.33325 2.66699 7.63173 2.66699 7.99992C2.66699 8.36811 2.96547 8.66659 3.33366 8.66659Z" stroke="#2C2D32" stroke-width="0.866667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        `;

        buttonsDiv.appendChild(resolveBtn);
        buttonsDiv.appendChild(moreActionsBtn);

        headDiv.appendChild(avatarDiv);
        headDiv.appendChild(creatorDiv);
        headDiv.appendChild(buttonsDiv);

        const commentTextDiv = document.createElement("div");
        commentTextDiv.className = "commentText";
        commentTextDiv.textContent = decodeHTMLEntities(comment.text ?? "");

        const answersCountDiv = document.createElement("div");
        answersCountDiv.className = "commentAnswersCount";
        if (answersCount) {
            answersCountDiv.textContent = t("comments.answers", { count: answersCount });
        }

        wrapper.appendChild(headDiv);
        wrapper.appendChild(commentTextDiv);
        wrapper.appendChild(answersCountDiv);

        return wrapper.outerHTML;
    }

    getCommentsTemplate() {
        return `<div class="commentsList scrollable"></div>`;
    }

    getEmptyTemplate() {
        return `
            <div class="commentsWindowEmpty">
                <img src="${canv.host}${EMPTY_COMMENTS}" alt="empty comments" class="commentsWindowEmptyImg">
                <p class="commentsWindowEmptyText">${t("comments.empty")}</p>
            </div>
        `;
    }

    comments: CommentWidget[] = [];
    selectedCommentUID: string | null = null;
    opened: boolean = false;

    settingsButton: Element | null = null;
    settingsMenu: Element | null = null;
    sortingButton: Element | null = null;
    sortingMenu: Element | null = null;

    _onItemClick = (e) => this.itemClick(e.currentTarget.id);
    _onResolve = (e) => this.itemResolve(e.currentTarget.closest(".commentsItem").id, e);
    _onMoreActions = (e) => this.openItemActions(e.currentTarget.closest(".commentsItem").id, e);
    _onItemMenuCopyLink = (e) =>
        this.itemMenuCopyLink(e.currentTarget.closest(".commentsItem").id, e);
    _onItemMenuDelete = (e) =>
        canv.canvasController.deleteWidget(e.currentTarget.closest(".commentsItem").id);

    sort = "createSort";
}
